<template>
  <section :id="formId" class="modal-form" :class="classes">
    <div class="modal-form__inner">
      <div v-show="!showResultForm" class="modal-form__content">
        <div class="modal-form__header">
          <h2 v-if="formTitle" class="modal-form__title">
            {{ normalizeHtmlText(formTitle) }}
          </h2>
          <div v-if="!isStepMode && formDescription" class="modal-form__description">
            {{ normalizeHtmlText(formDescription) }}
          </div>
        </div>

        <FormStepper v-if="showFormStepper" :current-step="currentStep" :step-idx="stepIdx" :steps-count="steps.length" />

        <CoreForm
          class="modal-form__form"
          :formConfig="formConfig"
          :action="action"
          :extra-field-names="extraFieldNames"
          :extra-submit-data="extraSubmitData"
          :before-submit-fn="beforeSubmitFn"
          :agreements="agreements"
          :back-label="backLabel"
          :next-label="nextLabel"
          :submit-label="submitLabel"
          @form:init="onFormInit"
          @form:validation-error="onValidationError"
          @form:back-step="onBackStep"
          @form:next-step="onNextStep"
          @form:submit-success="onSuccessSubmit"
          @form:submit-error="onErrorSubmit"
        >
          <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps" />
          </template>
        </CoreForm>
      </div>
    </div>
  </section>
</template>

<script>
import { FORM_STATUS } from '@shared/const/form/form.status'
import FormStepper from '@shared/ui/public/forms/components/FormStepper.vue'
import FormActions from '@shared/ui/public/forms/components/FormActions.vue'
import FormErrorResult from '@shared/ui/public/forms/components/FormErrorResult.vue'
import FormSuccessResult from '@shared/ui/public/forms/components/FormSuccessResult.vue'
import CoreForm from '@shared/ui/public/forms/CoreForm.vue'
import { DEFAULT_AGREEMENTS, DEFAULT_EXTRA_FIELD_NAMES } from '@shared/const/form/form.field'
import { FORM_VARIANTS } from '@shared/const/form/form.data'
import { normalizeHtmlText } from '@shared/utils/string'

export default {
  name: 'PageForm',
  emits: ['submit:success', 'submit:error'],
  components: {
    FormStepper,
    FormActions,
    FormErrorResult,
    FormSuccessResult,
    CoreForm,
  },
  props: {
    formConfig: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: true,
    },
    extraFieldNames: {
      type: Array,
      default: () => DEFAULT_EXTRA_FIELD_NAMES,
    },
    extraSubmitData: {
      type: Object,
      default: () => ({}),
    },
    beforeSubmitFn: {
      type: Function,
      default: $ => $,
    },
    agreements: {
      type: Array,
      default: () => DEFAULT_AGREEMENTS,
    },
    backLabel: {
      type: String,
      default: window.staticText.forms.back,
    },
    nextLabel: {
      type: String,
      default: window.staticText.forms.then,
    },
    submitLabel: {
      type: String,
      default: window.staticText.forms.regBtn,
    },
  },
  data() {
    return {
      formState: null,
      publicApi: null,
    }
  },
  computed: {
    form() {
      return this.formState?.form || {}
    },
    formId() {
      return this.publicApi?.formId()
    },
    formStatus() {
      return this.formState?.status || ''
    },
    currentStep() {
      return this.steps[this.stepIdx]
    },
    stepIdx() {
      return this.formState?.currentStep || 0
    },
    steps() {
      return this.form.steps || []
    },
    fields() {
      return this.formState?.fields || []
    },
    showFormStepper() {
      return this.currentStep?.title || this.currentStep?.description
    },
    formTitle() {
      return this.form.title
    },
    formDescription() {
      return this.form.description
    },
    isStepMode() {
      return this.form.mode === FORM_VARIANTS.STEPS
    },
    classes() {
      if (!this.formStatus) return []

      return [
        `modal-form--status-${this.formStatus}`,
        `modal-form--mode-${this.form.mode}`,
        `modal-form--theme-${this.form.theme}`,
        {
          'modal-form--submitting': this.formStatus === FORM_STATUS.SUBMITTING,
          'modal-form--success': this.formStatus === FORM_STATUS.SUCCESS,
          'modal-form--error': this.formStatus === FORM_STATUS.ERROR,
        },
      ]
    },
    showResultForm() {
      return [FORM_STATUS.SUCCESS, FORM_STATUS.ERROR].includes(this.formStatus)
    },
  },
  methods: {
    normalizeHtmlText,
    onFormInit({ formState, publicApi }) {
      this.formState = formState
      this.publicApi = publicApi
    },
    onSuccessSubmit() {
      this.$emit('submit:success')
    },
    onErrorSubmit() {
      this.$emit('submit:error')
    },
    onValidationError({ formId }) {
      this.$nextTick(() => {
        this.$root.scrollToBlock(`#${formId} .form-fields__item--error`)
      })
    },
    onBackStep({ formId }) {
      this.$root.scrollToBlock(`#${formId}`)
    },
    onNextStep({ formId }) {
      this.$root.scrollToBlock(`#${formId}`)
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.modal-form
  &--theme-cyan
    --modal-form-bg-color: #{$cyan}
    --modal-form-text-color: #{$white}
    --form-stepper-track-color: #33C0CA
    --form-stepper-value-color: #99DFE5

  &--theme-white
    --modal-form-bg-color: #{$white}
    --modal-form-text-color: #{$black}
    --form-stepper-track-color: #99DFE5
    --form-stepper-value-color: #33C0CA

  background-color: var(--modal-form-bg-color)
  color: var(--modal-form-text-color)
  transition: background-color .3s ease, opacity .3s ease

  &--success
    background: $cyan
    color: #ffffff

  &--error
    background: #EB7C7C
    color: #ffffff

  &__inner
    width: 100%

  &__header
    margin-bottom: 32px

  &__title
    margin: 0 0 16px
    color: var(--modal-form-text-color)

  &__description
    line-height: 1.4

  &__form
    display: flex
    flex-direction: column
    gap: 24px
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/ModalForm.vue